import { read, utils } from "xlsx";

export class FileReadError extends Error {}

const stripBom = (text) => text.replace(/^\uFEFF/, "");

/**
 * CSV bytes as text. Excel on Windows saves "CSV" in the ANSI code page (Windows-1252), not UTF-8,
 * so a file that is not valid UTF-8 is read as Windows-1252 instead of turning its non-ASCII
 * characters into replacement marks (which would make a SKU or a header unrecognisable).
 */
export const decodeCsv = (bytes) => {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
};

/**
 * Rows of the first sheet as `{ row, cells }`, `row` being the row number the file shows (so an
 * error can say "baris 12" and mean row 12 in Excel). Blank rows are kept, to keep that numbering.
 *
 * Parsed with `raw: true`, so nothing is guessed: a numeric xlsx cell stays a number, and every
 * CSV cell stays text. Without it the CSV reader "fuzzy-parses" text, reading `15.000` as 15 and
 * `15,5` as 155, which is how a price silently turns into a different price.
 * The CSV delimiter (`,` `;` tab `|`, or a `sep=` line) is detected by the reader.
 */
export const readSheetRowsFromData = (data, type) => {
  let workbook;
  try {
    workbook = read(data, { type, raw: true, cellDates: false });
  } catch {
    throw new FileReadError(
      "File tidak dapat dibaca. Pastikan file berformat .xlsx atau .csv yang valid."
    );
  }

  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  if (!sheet || !sheet["!ref"]) {
    throw new FileReadError("File tidak berisi data.");
  }

  const firstRow = utils.decode_range(sheet["!ref"]).s.r;
  const matrix = utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: true,
  });

  return matrix.map((cells, index) => ({ row: firstRow + index + 1, cells }));
};

export const readSheetRows = async (file) => {
  const isCsv = /\.csv$/i.test(file.name) || file.type === "text/csv";

  if (isCsv) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    return readSheetRowsFromData(stripBom(decodeCsv(bytes)), "string");
  }

  return readSheetRowsFromData(new Uint8Array(await file.arrayBuffer()), "array");
};
